import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, TruckIcon, PackageIcon, Sparkles, CheckCircle2, TruckIcon as TruckEmpty, PackageCheck } from 'lucide-react';
import { AlertFeed } from './AlertFeed';
import { DriverList } from './DriverList';
import { DeliveryList } from './DeliveryList';
import { TriagePanel } from './TriagePanel';
import { Alert, Driver, Delivery, TriageItem } from '../data/mockData';
type PanelView = 'alerts' | 'fleet' | 'deliveries' | 'triage' | null;
interface LeftPanelProps {
  alerts: Alert[];
  drivers: Driver[];
  deliveries: Delivery[];
  triageItems: TriageItem[];
  selectedAlertId: string | null;
  selectedDriverId: string | null;
  onSelectAlert: (alertId: string, driverId: string) => void;
  onSelectDriver: (driverId: string) => void;
  onSelectDeliveryDriver: (driverId: string, deliveryRef?: string) => void;
  requestedAlertFilter?: {
    severity: 'all' | 'critical' | 'warning' | 'low';
    ts: number;
  } | null;
}
export function LeftPanel({
  alerts,
  drivers,
  deliveries,
  triageItems,
  selectedAlertId,
  selectedDriverId,
  onSelectAlert,
  onSelectDriver,
  onSelectDeliveryDriver,
  requestedAlertFilter
}: LeftPanelProps) {
  const [activeView, setActiveView] = useState<PanelView>(null);
  const [alertTab, setAlertTab] = useState<'active' | 'resolved'>('active');
  const [fleetTab, setFleetTab] = useState<'active' | 'inactive'>('active');
  const [deliveryTab, setDeliveryTab] = useState<'enRoute' | 'delivered' | 'undelivered'>('enRoute');
  const criticalCount = alerts.filter(a => a.severity === 'critical' || a.severity === 'warning').length;
  useEffect(() => {
    if (requestedAlertFilter) {
      setActiveView('alerts');
    }
  }, [requestedAlertFilter]);
  const handleToggle = (view: PanelView) => {
    setActiveView(prev => prev === view ? null : view);
  };
  const panelHeaders: Record<string, {
    title: string;
    subtitle: string;
  }> = {
    alerts: {
      title: 'Active Alerts',
      subtitle: `${alerts.length} alerts · ${criticalCount} need attention`
    },
    fleet: {
      title: 'Fleet',
      subtitle: `${drivers.length} drivers · ${drivers.reduce((sum, d) => sum + d.cargo.length, 0)} active shipments`
    },
    deliveries: {
      title: 'Deliveries',
      subtitle: `${deliveries.length} total · ${deliveries.filter(d => d.status === 'at-risk').length} at risk`
    },
    triage: {
      title: 'AI Triage',
      subtitle: "AI-assisted recommendations · You're always in control"
    }
  };
  const [hasUnreadAlerts, setHasUnreadAlerts] = useState(criticalCount > 0);
  const activeDrivers = drivers.filter(d => d.activityStatus !== 'sleeper');
  const inactiveDrivers = drivers.filter(d => d.activityStatus === 'sleeper');
  const enRouteDeliveries = deliveries.filter(d => d.status === 'pending' || d.status === 'at-risk');
  const deliveredDeliveries = deliveries.filter(d => d.status === 'completed');
  const undeliveredDeliveries: Delivery[] = [];
  return <div className="flex h-full shrink-0 z-10" data-id="element-324">
      {/* Icon Rail */}
      <div className="w-[72px] h-full bg-slate-950/95 border-r border-slate-800 flex flex-col items-center pt-4 gap-2 shrink-0" data-id="element-325">
        <RailButton icon={<AlertTriangle className="w-5 h-5" data-id="element-327" />} label="Alerts" isActive={activeView === 'alerts'} onClick={() => {
        handleToggle('alerts');
        setHasUnreadAlerts(false);
      }} hasUnread={hasUnreadAlerts} data-id="element-326" />
        <RailButton icon={<TruckIcon className="w-5 h-5" data-id="element-329" />} label="Fleet" isActive={activeView === 'fleet'} onClick={() => handleToggle('fleet')} data-id="element-328" />
        <RailButton icon={<PackageIcon className="w-5 h-5" data-id="element-331" />} label="Deliveries" isActive={activeView === 'deliveries'} onClick={() => handleToggle('deliveries')} data-id="element-330" />
        <RailButton icon={<Sparkles className="w-5 h-5" data-id="element-333" />} label="Triage" isActive={activeView === 'triage'} onClick={() => handleToggle('triage')} data-id="element-332" />
      </div>

      {/* Expandable Panel */}
      <AnimatePresence data-id="element-334">
        {activeView && <motion.div initial={{
        width: 0,
        opacity: 0
      }} animate={{
        width: 400,
        opacity: 1
      }} exit={{
        width: 0,
        opacity: 0
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 320
      }} className="h-full overflow-hidden border-r border-slate-800 bg-slate-950/90 backdrop-blur-md flex flex-col" data-id="element-335">
            <div className="w-[400px] h-full flex flex-col" data-id="element-336">
              {/* Panel Header */}
              <div className="shrink-0 border-b border-slate-800" data-id="element-337">
                {activeView === 'alerts' ? <div className="flex px-4 pt-3" data-id="element-338">
                    {(['active', 'resolved'] as const).map(tab => <button key={tab} onClick={() => setAlertTab(tab)} className={`relative px-4 pb-2.5 text-sm font-medium transition-colors ${alertTab === tab ? 'text-slate-100' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-339">
                        {tab === 'active' ? 'Active' : 'Resolved'}
                        {alertTab === tab && <motion.div layoutId="alertTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand-blue rounded-full" transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 400
                }} data-id="element-340" />}
                      </button>)}
                  </div> : activeView === 'fleet' ? <div className="flex px-4 pt-3" data-id="element-341">
                    {(['active', 'inactive'] as const).map(tab => <button key={tab} onClick={() => setFleetTab(tab)} className={`relative px-4 pb-2.5 text-sm font-medium transition-colors ${fleetTab === tab ? 'text-slate-100' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-342">
                        {tab === 'active' ? 'Active' : 'Inactive'}
                        {fleetTab === tab && <motion.div layoutId="fleetTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand-blue rounded-full" transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 400
                }} data-id="element-343" />}
                      </button>)}
                  </div> : activeView === 'deliveries' ? <div className="flex px-4 pt-3" data-id="element-344">
                    {(['enRoute', 'delivered', 'undelivered'] as const).map(tab => <button key={tab} onClick={() => setDeliveryTab(tab)} className={`relative px-4 pb-2.5 text-sm font-medium transition-colors ${deliveryTab === tab ? 'text-slate-100' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-345">
                          {tab === 'enRoute' ? 'En Route' : tab === 'delivered' ? 'Delivered' : 'Undelivered'}
                          {deliveryTab === tab && <motion.div layoutId="deliveryTabIndicator" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand-blue rounded-full" transition={{
                  type: 'spring',
                  damping: 30,
                  stiffness: 400
                }} data-id="element-346" />}
                        </button>)}
                  </div> : <div className="p-4 pb-2" data-id="element-347">
                    <h2 className="text-sm font-semibold text-slate-100 flex items-center gap-2" data-id="element-348">
                      {activeView === 'triage' && <Sparkles className="w-4 h-4 text-brand-blue" data-id="element-349" />}
                      {panelHeaders[activeView].title}
                    </h2>
                  </div>}
              </div>

              {/* Panel Content */}
              {activeView === 'alerts' && alertTab === 'active' && <AlertFeed alerts={alerts} selectedAlertId={selectedAlertId} onSelectAlert={onSelectAlert} requestedSeverityFilter={requestedAlertFilter} data-id="element-350" />}
              {activeView === 'alerts' && alertTab === 'resolved' && <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-351">
                  <CheckCircle2 className="w-8 h-8 text-brand-green/30 mb-3" data-id="element-352" />
                  <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-353">
                    No resolved alerts
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-354">
                    Resolved alerts will appear here
                  </p>
                </div>}
              {activeView === 'fleet' && fleetTab === 'active' && <DriverList drivers={activeDrivers} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDriver} variant="active" data-id="element-355" />}
              {activeView === 'fleet' && fleetTab === 'inactive' && (inactiveDrivers.length > 0 ? <DriverList drivers={inactiveDrivers} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDriver} variant="inactive" data-id="element-356" /> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-357">
                    <TruckIcon className="w-8 h-8 text-slate-700 mb-3" data-id="element-358" />
                    <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-359">
                      No inactive drivers
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-360">
                      Drivers in sleeper berth will appear here
                    </p>
                  </div>)}
              {activeView === 'deliveries' && deliveryTab === 'enRoute' && <DeliveryList deliveries={enRouteDeliveries} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDeliveryDriver} data-id="element-361" />}
              {activeView === 'deliveries' && deliveryTab === 'delivered' && (deliveredDeliveries.length > 0 ? <DeliveryList deliveries={deliveredDeliveries} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDeliveryDriver} showFilters={false} data-id="element-362" /> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-363">
                    <PackageIcon className="w-8 h-8 text-brand-green/30 mb-3" data-id="element-364" />
                    <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-365">
                      No completed deliveries yet
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-366">
                      Completed deliveries will appear here
                    </p>
                  </div>)}
              {activeView === 'deliveries' && deliveryTab === 'undelivered' && (undeliveredDeliveries.length > 0 ? <DeliveryList deliveries={undeliveredDeliveries} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDeliveryDriver} data-id="element-367" /> : <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-368">
                    <PackageIcon className="w-8 h-8 text-brand-red/30 mb-3" data-id="element-369" />
                    <p className="text-sm text-slate-400 font-medium mb-1" data-id="element-370">
                      No undelivered items
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-371">
                      Failed or returned deliveries will appear here
                    </p>
                  </div>)}
              {activeView === 'triage' && <TriagePanel items={triageItems} selectedDriverId={selectedDriverId} onSelectDriver={onSelectDriver} data-id="element-372" />}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function RailButton({
  icon,
  label,
  isActive,
  onClick,
  hasUnread
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  hasUnread?: boolean;
}) {
  return <button onClick={onClick} className={`relative flex flex-col items-center gap-1.5 w-14 py-2.5 rounded-xl transition-all
        ${isActive ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300 hover:bg-slate-900'}`} data-id="element-373">
      <div className="relative" data-id="element-374">
        {icon}
        {hasUnread && !isActive && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-brand-red rounded-full border-2 border-slate-950" data-id="element-375" />}
      </div>
      <span className="text-[10px] font-medium leading-none" data-id="element-376">{label}</span>
    </button>;
}